import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Image3 from "../assests/tabs-section-baner.png";
import { api } from "../services/api";
import StoreProductCard, { StoreStars } from "./StoreProductCard";
import { resolveProductImage } from "../utils/productAssets";
import { renderHtml } from "../utils/renderHtml";
import { formatCount } from "../utils/formatCount";
import { getDisplaySoldCount } from "../utils/displaySoldCount";

const TABS = [
  { key: "featured", label: "Featured products" },
  { key: "popular", label: "Popular products" },
  { key: "mostViewed", label: "Most viewed products" },
  { key: "topSelling", label: "Top selling" },
];

function TabsSection() {
  const [activeTab, setActiveTab] = useState("featured");
  const [tabProducts, setTabProducts] = useState([]);
  const [latestProducts, setLatestProducts] = useState([]);
  const [bannerProducts, setBannerProducts] = useState([]);
  const [heroProduct, setHeroProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get(`/listings?section=${activeTab}&limit=15`)
      .then((data) => {
        if (mounted) setTabProducts(data.products || []);
      })
      .catch(() => {
        if (mounted) setTabProducts([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [activeTab]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/listings?limit=5"),
      api.get("/listings?section=topSelling&limit=2"),
      api.get("/listings?section=machines&limit=1"),
    ])
      .then(([latest, banner, hero]) => {
        if (!mounted) return;
        setLatestProducts(latest.products || []);
        setBannerProducts(banner.products || []);
        setHeroProduct(hero.products?.[0] || null);
      })
      .catch(() => {
        if (!mounted) return;
        setLatestProducts([]);
        setBannerProducts([]);
        setHeroProduct(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!tabProducts.length && !latestProducts.length && !loading) return null;

  return (
    <section className="bg-slate-50 py-8">
      <div className="container">
        <div className="grid items-start gap-5 lg:grid-cols-[320px_1fr]">
          <aside className="flex flex-col gap-4">
            <div className="h-[450px] overflow-hidden rounded-xl shadow">
              <img alt="Sewing" className="h-full w-full object-cover" src={Image3} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 inline-block border-b-2 border-[#3991D5] pb-2 font-gopher text-base font-bold text-[#3991D5]">Latest Products</h3>
              <div className="flex flex-col gap-3">
                {latestProducts.map((product) => (
                  <Link className="flex gap-3 rounded-lg p-2 transition hover:bg-blue-50" key={product._id || product.sku} to={`/products/${product.slug || product.sku}`}>
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-lg bg-slate-50 p-1">
                      <img alt={product.name} className="max-h-full max-w-full object-contain" src={resolveProductImage(product.image)} />
                    </div>
                    <div className="min-w-0">
                      <p className="mb-1 line-clamp-2 text-sm font-bold leading-5 text-slate-900">{product.name}</p>
                      <p className="mt-1 text-xs font-black text-[#3991D5]">${Number(product.price || 0).toFixed(2)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          <main className="flex flex-col gap-5">
            <div className="rounded-xl border border-slate-200 bg-white px-4 shadow-sm">
              <div className="flex justify-end gap-7 overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    className={`relative whitespace-nowrap border-0 bg-transparent py-3 text-sm font-bold ${activeTab === tab.key ? "text-[#3991D5]" : "text-slate-500"}`}
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    type="button"
                  >
                    {tab.label}
                    {activeTab === tab.key && <span className="absolute bottom-0 left-0 h-0.5 w-full bg-[#3991D5]" />}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div className="h-64 animate-pulse rounded-xl border border-slate-200 bg-white" key={index} />
                ))}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                {tabProducts.map((product) => (
                  <StoreProductCard compact key={product._id || product.sku} product={product} />
                ))}
              </div>
            )}
          </main>
        </div>
        {heroProduct && (
          <div className="mt-4 flex flex-col gap-5 overflow-hidden rounded-2xl bg-[#2196f3] p-5 shadow-xl md:flex-row md:items-start">
            <div className="flex flex-1 items-center gap-5 text-white">
              <div className="mt-5 h-[200px] items-center justify-start">
                <img alt={heroProduct.name} className="max-h-full max-w-md rounded-xl object-contain" src={resolveProductImage(heroProduct.image)} />
              </div>
              <div className="flex-1">
                <p className="font-gopher text-sm font-bold italic text-white/80">Installment Simplify</p>
                <h2 className="mt-1 font-gopher text-3xl font-black text-[#EAEBB8]">{heroProduct.name}</h2>
                <p className="mt-2 max-w-md text-sm leading-6 text-white/85" dangerouslySetInnerHTML={renderHtml(heroProduct.shortDescription || heroProduct.description)} />
              </div>
            </div>
            <div className="grid w-[35%] gap-3 sm:grid-cols-2">
              {bannerProducts.map((product) => (
                <Link className="rounded-xl bg-white p-4 text-center shadow transition hover:-translate-y-1" key={product._id || product.sku} to={`/products/${product.slug || product.sku}`}>
                  <div className="mb-3 flex h-24 items-center justify-center rounded-lg bg-slate-50 p-2">
                    <img alt={product.name} className="max-h-full max-w-full object-contain" src={resolveProductImage(product.image)} />
                  </div>
                  <p className="line-clamp-2 text-sm font-bold text-slate-900">{product.name}</p>
                  <StoreStars count={Number(product.averageRating || product.rating || 4)} />
                  <p className="mt-1 text-[11px] font-semibold text-slate-400">
                    {formatCount(product.reviewCount || 0)} review{Number(product.reviewCount || 0) === 1 ? "" : "s"}
                    {` · ${formatCount(getDisplaySoldCount(product))} sold`}
                  </p>
                  <p className="mt-1 text-sm font-black text-[#3991D5]">${Number(product.price || 0).toFixed(2)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default TabsSection;
